"use client";

import { useState } from "react";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";
import { Activity, Copy, ExternalLink, Pencil, Trash2 } from "lucide-react";
import { TableActionCell, type TableActionItem } from "@/ui";
import ConfirmDialog from "@components/shared/ConfirmDialog";
import { FLOAT_STATUS } from "@lib/enums";

const MENU_ICONS = {
  published: <ExternalLink size={16} aria-hidden="true" />,
  edit: <Pencil size={16} aria-hidden="true" />,
  activity: <Activity size={16} aria-hidden="true" />,
  duplicate: <Copy size={16} aria-hidden="true" />,
  delete: <Trash2 size={16} aria-hidden="true" />,
};

interface FloatMenuItem {
  uuid: string;
  status: string;
}

interface MoreMenuProps {
  float: FloatMenuItem;
  onDuplicate: (uuid: string) => void;
  onDelete: (uuid: string) => void;
}

export default function MoreMenu({ float, onDuplicate, onDelete }: MoreMenuProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const router = useRouter();

  const handleView = () => {
    router.push(`/floats/${float.uuid}`);
  };

  const handleContinueDraft = () => {
    router.push(`/floats/${float.uuid}/edit?step=1`);
  };

  const handleEdit = () => {
    router.push(`/floats/${float.uuid}/edit?step=1`);
  };

  const handleDuplicate = () => {
    onDuplicate(float.uuid);
  };

  const handleViewInteractive = () => {
    window.open(`/show/${float.uuid}?p=1`, "_blank");
  };

  const handleDelete = () => {
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    onDelete(float.uuid);
    toast.success("Float deleted");
  };

  const sharedItems: TableActionItem[] = [
    { label: "Duplicate", onClick: handleDuplicate, icon: MENU_ICONS.duplicate },
    {
      label: "Delete",
      onClick: handleDelete,
      destructive: true,
      icon: MENU_ICONS.delete,
    },
  ];

  const items: TableActionItem[] =
    float.status === FLOAT_STATUS.DRAFT
      ? [
          {
            label: "Continue Draft",
            onClick: handleContinueDraft,
            icon: MENU_ICONS.edit,
          },
          ...sharedItems,
        ]
      : [
          {
            label: "Details & activity",
            onClick: handleView,
            icon: MENU_ICONS.activity,
          },
          {
            label: "Preview",
            onClick: handleViewInteractive,
            icon: MENU_ICONS.published,
          },
          { label: "Edit", onClick: handleEdit, icon: MENU_ICONS.edit },
          ...sharedItems,
        ];

  return (
    <>
      <TableActionCell items={items} />
      <ConfirmDialog
        title="Delete float"
        message="Are you sure you want to delete this float?"
        open={confirmOpen}
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
